import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useBack, useForm, useNotification, useOne } from '@refinedev/core';
import {
  Box,
  Typography,
  Button,
  Stack,
  Paper,
  Container,
  CircularProgress,
  Alert,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useForm as useReactHookForm, FormProvider } from 'react-hook-form';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import {
  EmaSchedulingEdition,
  SchedulingFrequency,
  SchedulingConfigurationForm,
} from '@/features/scheduling/components';
import {
  CreateSchedulingsForQuestionnaireDTO,
  GetSchedulingsForQuestionnaireDTO,
} from '@aphp-ema/common/scheduling';
import {
  useGetQuestionnaireScheduling,
  useCreateQuestionnaireScheduling,
} from '@/features/scheduling/api';
import dayjs from 'dayjs';
import { SchedulingFormData } from '@/features/scheduling/types';
import { schedulingOverlapValidator } from '@/features/scheduling/domain';
import { REMINDER_DEFAULTS } from '@/features/scheduling/constants/reminderDefaults';
import { zodResolver } from '@hookform/resolvers/zod';
import { createSchedulingFormSchema } from '@/features/scheduling/hooks/useSchedulingFormSchema';
import type { EmaQuestionnaire } from '@aphp-ema/common/questionnaire';
export const EditSchedulingPage: React.FC = () => {
  const { questionnaireId } = useParams<{ questionnaireId: string }>();

  const {
    data: questionnaireData,
    isLoading: isQuestionnaireLoading,
    error: questionnaireError,
  } = useOne<EmaQuestionnaire>({
    resource: 'ema-questionnaire',
    id: questionnaireId || '',
    queryOptions: {
      enabled: !!questionnaireId,
    },
  });

  // Fetch EMA questionnaire Notification Schedules
  const { data: schedulingData } = useGetQuestionnaireScheduling(
    questionnaireId || '',
  );

  if (isQuestionnaireLoading) {
    return (
      <Container maxWidth="md">
        <Box sx={{ py: 4, display: 'flex', justifyContent: 'center' }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (questionnaireError) {
    return (
      <Container maxWidth="md">
        <Box sx={{ py: 4 }}>
          <Alert severity="error">Failed to load questionnaire data</Alert>
        </Box>
      </Container>
    );
  }

  return (
    <EditSchedulingPageContent
      questionnaireId={questionnaireId || ''}
      schedulingData={schedulingData}
      questionnaireData={questionnaireData.data}
    />
  );
};

const EditSchedulingPageContent = ({
  questionnaireId,
  schedulingData,
  questionnaireData,
}: {
  questionnaireId: string;
  schedulingData?: GetSchedulingsForQuestionnaireDTO;
  questionnaireData?: EmaQuestionnaire;
}) => {
  const { t } = useTranslation();
  const { open: openNotification } = useNotification();

  const { formLoading } = useForm<SchedulingFormData>({
    resource: 'questionnaire-scheduling',
    action: 'edit',
    id: questionnaireId,
  });

  const hasSpecificDaysScheduling = (
    schedulings: GetSchedulingsForQuestionnaireDTO['schedulings'] | undefined,
  ): boolean => {
    return (
      schedulings !== undefined &&
      schedulings.some((s) => s.day !== 'EVERY_DAY')
    );
  };

  const [selectedFrequency, setSelectedFrequency] =
    useState<SchedulingFrequency>(
      hasSpecificDaysScheduling(schedulingData?.schedulings)
        ? 'specific'
        : 'daily',
    );

  // Create scheduling mutation
  const createSchedulingMutation = useCreateQuestionnaireScheduling();

  const formMethods = useReactHookForm<SchedulingFormData>({
    defaultValues: {
      schedulings: schedulingData?.schedulings.map((n) => ({
        day: n.day,
        startTime: dayjs(n.startTime, 'HH:mm'),
        endTime: n.endTime && dayjs(n.endTime, 'HH:mm'),
      })),
      configuration: {
        ...REMINDER_DEFAULTS,
        ...schedulingData?.configuration,
      },
    },
    resolver: zodResolver(createSchedulingFormSchema(t)),
    mode: 'onChange',
  });

  // TODO : ugly hack to remove by using Refine useForm properly
  useEffect(() => {
    formMethods.reset({
      schedulings: schedulingData?.schedulings.map((n) => ({
        day: n.day,
        startTime: dayjs(n.startTime, 'HH:mm'),
        endTime: n.endTime && dayjs(n.endTime, 'HH:mm'),
      })),
      configuration: {
        ...REMINDER_DEFAULTS,
        ...schedulingData?.configuration,
      },
    });
  }, [schedulingData, formMethods]);

  const { control, handleSubmit } = formMethods;

  const back = useBack();

  const onSubmit = (data: SchedulingFormData) => {
    if (!questionnaireId) {
      return;
    }

    // Filter notifications based on selected frequency
    const filteredSchedulings = data.schedulings.filter((scheduling) => {
      if (selectedFrequency === 'daily') {
        return scheduling.day === 'EVERY_DAY';
      } else {
        return scheduling.day !== 'EVERY_DAY';
      }
    });

    const schedulingData: CreateSchedulingsForQuestionnaireDTO = {
      questionnaireId,
      schedulings: filteredSchedulings.map((notification) => ({
        day: notification.day,
        startTime: notification.startTime?.format('HH:mm') || '09:00',
        endTime: notification.endTime?.format('HH:mm'),
      })),
      configuration: data.configuration,
    };

    const overlapValidationResult = schedulingOverlapValidator(schedulingData);

    if (!overlapValidationResult.isValid) {
      openNotification?.({
        message: t('pages.questionnaireScheduling.edit.overlapError'),
        type: 'error',
      });
      return;
    }

    createSchedulingMutation.mutate(schedulingData, {
      onSuccess: () => {
        back();
      },
    });
  };

  return (
    <Container maxWidth="md">
      <Box sx={{ py: 4 }}>
        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Button
              startIcon={<ArrowBackIcon />}
              onClick={back}
              sx={{ p: 0, minWidth: 'auto' }}
              variant="text"
            />

            <Typography variant="h4" component="h1">
              {t('pages.questionnaireScheduling.edit.title')}
            </Typography>
          </Box>
          {questionnaireData && (
            <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>
              {t('pages.studies.emaQuestionnaire.show.title', {
                title: questionnaireData.title,
              })}
            </Typography>
          )}
        </Box>

        <Paper sx={{ p: 3 }}>
          <FormProvider {...formMethods}>
            <form onSubmit={handleSubmit(onSubmit)}>
              <EmaSchedulingEdition
                control={control}
                onFrequencyChange={setSelectedFrequency}
                selectedFrequency={selectedFrequency}
              />

              <SchedulingConfigurationForm control={control} />

              <Stack
                direction="row"
                spacing={2}
                sx={{ justifyContent: 'flex-end', mt: 3 }}
              >
                <Button variant="outlined" onClick={back}>
                  {t('buttons.cancel')}
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  disabled={formLoading || createSchedulingMutation.isLoading}
                >
                  {createSchedulingMutation.isLoading ? (
                    <CircularProgress size={20} />
                  ) : (
                    t('buttons.save')
                  )}
                </Button>
              </Stack>
            </form>
          </FormProvider>
        </Paper>
      </Box>
    </Container>
  );
};
